import { startOfDay, parseISO, isValid } from "date-fns";

/** Canonical timezone: same calendar logic as Pakistan for stats, shifts, and labels. */
export const REVENUE_REPORT_TIMEZONE = "Asia/Karachi";

function formatInstantInReportTz(
  d: Date,
  kind: "yyyy-MM-dd" | "MM/dd/yyyy"
): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: REVENUE_REPORT_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const year = parts.find((p) => p.type === "year")?.value ?? "";
  const mo = parts.find((p) => p.type === "month")?.value ?? "";
  const da = parts.find((p) => p.type === "day")?.value ?? "";
  if (!year || !mo || !da) return "";
  if (kind === "yyyy-MM-dd") return `${year}-${mo}-${da}`;
  return `${mo}/${da}/${year}`;
}

/** Calendar YYYY-MM-DD in Asia/Karachi for an instant. */
export function instantToReportYmd(d: Date): string {
  return formatInstantInReportTz(d, "yyyy-MM-dd");
}

function parseShiftDate(value: string | number | Date): Date | null {
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);
  if (typeof value === "string" && value !== "") {
    const iso = parseISO(value);
    if (isValid(iso)) return iso;
    const t = Date.parse(value);
    if (!Number.isNaN(t)) return new Date(t);
  }
  return null;
}

/**
 * Calendar YYYY-MM-DD in Asia/Karachi for a Firestore Timestamp.
 */
export function revenueDailyStatReportYmd(
  dateField: { toDate: () => Date } | undefined | null
): string {
  if (!dateField || typeof dateField.toDate !== "function") return "";
  return instantToReportYmd(dateField.toDate());
}

/**
 * Inclusive YYYY-MM-DD range aligned with {@link instantToReportYmd} (Asia/Karachi),
 * so comparisons to daily stat / shift days are consistent in every browser timezone.
 *
 * Uses start-of-day in the user's local calendar for each bound (so endOf('day') on the
 * last selected night still maps to that same calendar day, not the next).
 */
export function revenueReportYmdRange(startDate: Date, endDate: Date) {
  return {
    startYmd: instantToReportYmd(startOfDay(startDate)),
    endYmd: instantToReportYmd(startOfDay(endDate)),
  };
}

/**
 * Calendar day for a Workforce shift. Prefer API `date` (YYYY-MM-DD) — Workforce
 * daily totals use this field, not clock-in timezone. Fall back to Karachi start.
 */
export function workforceShiftReportYmd(shift: {
  shiftData?: {
    date?: string | number | Date | null;
    start?: number | null;
  };
} | null | undefined): string {
  const d = shift?.shiftData;
  if (!d) return "";
  if (d.date != null && d.date !== "") {
    if (typeof d.date === "string") {
      const m = d.date.match(/^(\d{4}-\d{2}-\d{2})/);
      if (m) return m[1];
    }
    const parsed = parseShiftDate(d.date as string | number | Date);
    if (!parsed) return "";
    return instantToReportYmd(parsed);
  }
  if (d.start != null) {
    return instantToReportYmd(new Date(d.start * 1000));
  }
  return "";
}

/**
 * Display MM/dd/yyyy for the stat's instant in Asia/Karachi (Pakistan-equivalent view).
 */
export function formatRevenueDateReportMMDDYYYY(date: any): string {
  if (!date) return "Invalid Date";
  if (typeof date.toDate === "function") {
    return formatInstantInReportTz(date.toDate(), "MM/dd/yyyy") || "Invalid Date";
  }
  if (date.seconds != null) {
    return (
      formatInstantInReportTz(
        new Date(date.seconds * 1000),
        "MM/dd/yyyy"
      ) || "Invalid Date"
    );
  }
  return "Invalid Date";
}

/** @deprecated Use revenueDailyStatReportYmd (Karachi). */
export const revenueDailyStatUtcYmd = revenueDailyStatReportYmd;
/** @deprecated Use workforceShiftReportYmd (Karachi). */
export const workforceShiftUtcYmd = workforceShiftReportYmd;
/** @deprecated Use formatRevenueDateReportMMDDYYYY (Karachi). */
export const formatRevenueDateUtcMMDDYYYY = formatRevenueDateReportMMDDYYYY;
